import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Spin, message, Descriptions, InputNumber, Button, Space } from 'antd';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { theme } from '../../theme';

interface PesajeData {
  numero_ticket: number;
  fecha_ticket: string;
  dtv: string;
  numero_remito: string;
  ingreso_egreso: string;
  compra_servicio: string;
  cliente_nombre: string;
  productor_nombre: string;
  transportista_nombre: string;
  chofer_nombre: string;
  camion_patente: string;
  producto_nombre: string;
  kilos_primera: number;
  kilos_segunda: number;
  humedad: number;
  establecimiento_nombre: string;
  nlote_nombre: string;
  variedad_semilla_nombre: string;
  presentacion_nombre: string;
  cantidad_rollos: number;
  puntaje_presentacion: number;
  ubicacion: string;
}

const SegundaPesada: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const registroId = params.registroId;
  const [loading, setLoading] = useState(true);
  const [savingData, setSavingData] = useState(false);
  const [pesaje, setPesaje] = useState<PesajeData | null>(null);
  const [datosListos, setDatosListos] = useState(false);
  const [segundaPesada, setSegundaPesada] = useState<number>(0);
  const [pesoNeto, setPesoNeto] = useState<number>(0);
  const { getAccessTokenSilently } = useAuth0();
  const { user } = useAuth0();
  const id_empresa = user?.id_empresa;
  const id_sucursal = user?.id_sucursal;

  useEffect(() => {
    const fetchPesaje = async () => {
      try {
        if (!registroId) {
          message.error('ID de registro no proporcionado');
          return;
        }

        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${process.env.REACT_APP_BALANZA_URL}/registro_balanza/${id_empresa}/${id_sucursal}/${registroId}/`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error('Error al obtener los datos del pesaje');
        }

        const responseData = await response.json();
        if (responseData.status === 'success' && responseData.data && responseData.data[0]) {
          setPesaje(responseData.data[0]);
          setDatosListos(true);
        }
      } catch (error) {
        console.error('Error:', error);
        message.error('Error al cargar los datos del pesaje');
      } finally {
        setLoading(false);
      }
    };

    fetchPesaje();
  }, [getAccessTokenSilently, registroId, id_empresa, id_sucursal]);

  const calcularPesoNeto = (segundoPeso: number) => {
    if (pesaje && pesaje.kilos_primera) {
      const neto = Math.abs(pesaje.kilos_primera - segundoPeso);
      setPesoNeto(neto);
    }
  };

  const handleSegundaPesadaChange = (value: number | null) => {
    if (value !== null) {
      setSegundaPesada(value);
      calcularPesoNeto(value);
    }
  };

  const handleGrabar = async () => {
    try {
      if (!registroId || !segundaPesada) {
        message.error('Debe ingresar el peso de la segunda pesada');
        return;
      }

      setSavingData(true);

      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_BALANZA_URL}/registro_balanza/${id_empresa}/${id_sucursal}/${registroId}/`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            kilos_segunda: segundaPesada
          })
        }
      );

      if (!response.ok) {
        throw new Error('Error al guardar la segunda pesada');
      }

      const responseData = await response.json();
      if (responseData.status === 'success') {
        message.success('Segunda pesada guardada correctamente');
        navigate('/listar_pesaje');
      } else {
        throw new Error(responseData.message || 'Error al guardar los datos');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al guardar la segunda pesada');
    } finally {
      setSavingData(false);
    }
  };

  const handleCancelar = () => {
    navigate('/listar_pesaje');
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!datosListos || !pesaje) {
    return <div>No se encontró el registro</div>;
  }

  return (
    <div style={{ padding: '0' }}>
      {/* Título de la página */}
      <h1 style={{ 
        color: theme.colors.primaryDark, 
        fontSize: '28px', 
        fontWeight: 600, 
        marginBottom: '24px' 
      }}>
        Segunda Pesada
      </h1>

      <Card
        style={{ 
          backgroundColor: '#ffffff',
          borderRadius: theme.styles.borderRadius,
          boxShadow: theme.styles.boxShadow,
          maxWidth: '1400px'
        }}
        styles={{
          header: {
            backgroundColor: theme.colors.primaryDark,
            color: 'white',
            borderRadius: '8px 8px 0 0',
            fontSize: '16px',
            fontWeight: 600,
            height: '56px',
            lineHeight: '56px'
          }
        }}
        title="Información del Pesaje"
      >
      <div style={{ padding: '24px' }}>
      <Descriptions 
        bordered 
        column={2}
        labelStyle={{
          fontWeight: 500,
          fontSize: '14px',
          color: theme.colors.textPrimary,
          backgroundColor: '#f9fafb'
        }}
        contentStyle={{
          fontSize: '14px',
          color: theme.colors.textSecondary
        }}
      >
        <Descriptions.Item label="Número de Ticket">
          {pesaje.numero_ticket}
        </Descriptions.Item>
        <Descriptions.Item label="Fecha">
          {pesaje.fecha_ticket}
        </Descriptions.Item>
        <Descriptions.Item label="DTV">
          {pesaje.dtv}
        </Descriptions.Item>
        <Descriptions.Item label="Remito">
          {pesaje.numero_remito}
        </Descriptions.Item>
        <Descriptions.Item label="Ingreso/Egreso">
          {pesaje.ingreso_egreso === 'I' ? 'Ingreso' : 'Egreso'}
        </Descriptions.Item>
        <Descriptions.Item label="Motivo">
          {pesaje.compra_servicio === 'C' ? 'Compra' :
           pesaje.compra_servicio === 'S' ? 'Servicio' :
           pesaje.compra_servicio === 'V' ? 'Venta' :
           pesaje.compra_servicio === 'A' ? 'A OtraSucursal' :
           pesaje.compra_servicio === 'D' ? 'Cambio Dueño' :
           pesaje.compra_servicio === 'E' ? 'Retiro Cliente' :
           pesaje.compra_servicio === 'B' ? 'Baja' :
           pesaje.compra_servicio === 'F' ? 'Desde Sucursal' :
           pesaje.compra_servicio === 'R' ? 'Recupero' : 'N/A'}
        </Descriptions.Item>
        <Descriptions.Item label="Cliente">
          {pesaje.cliente_nombre}
        </Descriptions.Item>
        <Descriptions.Item label="Productor">
          {pesaje.productor_nombre}
        </Descriptions.Item>
        <Descriptions.Item label="Transportista">
          {pesaje.transportista_nombre}
        </Descriptions.Item>
        <Descriptions.Item label="Chofer">
          {pesaje.chofer_nombre}
        </Descriptions.Item>
        <Descriptions.Item label="Patente">
          {pesaje.camion_patente}
        </Descriptions.Item>
        <Descriptions.Item label="Producto">
          {pesaje.producto_nombre}
        </Descriptions.Item>
        <Descriptions.Item label="Primera Pesada">
          {pesaje.kilos_primera} kg
        </Descriptions.Item>
        <Descriptions.Item label="Segunda Pesada">
          <InputNumber
            min={0}
            size="large"
            style={{ width: '150px' }}
            value={segundaPesada}
            onChange={handleSegundaPesadaChange}
            addonAfter="kg"
          />
        </Descriptions.Item>
      </Descriptions>
      
      {/* Peso Neto Centrado */}
      <div style={{ 
        textAlign: 'center', 
        marginTop: '24px',
        height: '56px',
        lineHeight: '56px',
        backgroundColor: theme.colors.primaryDark,
        borderRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div>
          <span style={{ 
            fontSize: '18px',
            color: 'white',
            marginRight: '15px',
            fontWeight: 500
          }}>
            Peso Neto:
          </span>
          <span style={{
            fontSize: '24px',
            fontWeight: 600,
            color: 'white'
          }}>
            {`${pesoNeto} kg`}
          </span>
        </div>
      </div>

      {/* Botones de acción */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'flex-end',
        gap: '12px',
        marginTop: '32px',
        paddingTop: '24px',
        borderTop: `1px solid ${theme.colors.borderLight}`
      }}>
        <Button
          onClick={handleCancelar}
          size="large"
          disabled={savingData}
          style={{
            fontWeight: 500,
            minWidth: '120px'
          }}
        >
          Cancelar
        </Button>
        <Button
          type="primary"
          onClick={handleGrabar}
          loading={savingData}
          size="large"
          style={{
            backgroundColor: theme.colors.secondary,
            borderColor: theme.colors.secondary,
            fontWeight: 500,
            minWidth: '120px'
          }}
        >
          {savingData ? 'Guardando...' : 'Grabar'}
        </Button>
      </div>
      </div>
      </Card>
    </div>
  );
};

export default SegundaPesada;